/**
 * karaokeEngine — pure Web Audio API procedural beat machine.
 * Generates royalty-free karaoke backing tracks live in the browser:
 *  - Kick, snare, hi-hat, sub-bass and chord pad — synthesised on the fly
 *  - 5 hand-tuned genre presets (Trap, Afro, Lo-fi, Pop, Drum&Bass)
 *  - Zero asset files → instant, offline-friendly, copyright-free.
 *
 * Usage:
 *   const eng = new KaraokeEngine();
 *   eng.start('trap');   // begin playing the trap preset
 *   eng.setVolume(0.7);
 *   eng.stop();
 */

// --------- Presets ---------
// Each preset is a 16-step pattern (1 bar @ 4/4, four 16th notes per beat).
// `1` = trigger, `0` = silent. Bass pattern uses note indices (0..7) or -1.
const PRESETS = {
  trap: {
    label: 'Trap', emoji: '🥁', bpm: 140,
    kick:  [1,0,0,0, 0,0,1,0, 0,0,0,0, 1,0,0,0],
    snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,1],
    hat:   [1,0,1,1, 1,1,0,1, 1,1,1,0, 1,0,1,1],
    bass:  [0,-1,-1,-1, 3,-1,-1,-1, 5,-1,-1,-1, 3,-1,2,-1],
    chord: { freq: 65.4, type: 'sawtooth', filter: 480 },
  },
  afro: {
    label: 'Afro', emoji: '🌍', bpm: 110,
    kick:  [1,0,0,0, 0,0,1,0, 0,1,0,0, 0,0,1,0],
    snare: [0,0,0,1, 1,0,0,0, 0,0,0,1, 0,1,0,0],
    hat:   [1,1,0,1, 1,0,1,1, 1,0,1,1, 1,1,0,1],
    bass:  [0,-1,-1,5, -1,2,-1,-1, 3,-1,5,-1, 2,-1,-1,0],
    chord: { freq: 130.8, type: 'triangle', filter: 800 },
  },
  lofi: {
    label: 'Lo-fi', emoji: '🌧️', bpm: 78,
    kick:  [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0],
    snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
    hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,1],
    bass:  [0,-1,-1,-1, -1,-1,-1,-1, 5,-1,-1,-1, -1,-1,-1,-1],
    chord: { freq: 110, type: 'sine', filter: 1200 },
  },
  pop: {
    label: 'Pop', emoji: '✨', bpm: 120,
    kick:  [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
    snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
    hat:   [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1],
    bass:  [0,-1,0,-1, 5,-1,5,-1, 3,-1,3,-1, 5,-1,5,-1],
    chord: { freq: 220, type: 'sawtooth', filter: 1400 },
  },
  dnb: {
    label: 'D&B', emoji: '⚡', bpm: 174,
    kick:  [1,0,0,0, 0,0,0,0, 0,0,1,0, 0,0,0,0],
    snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
    hat:   [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1],
    bass:  [0,-1,3,-1, -1,5,-1,2, 0,-1,3,-1, 5,-1,2,-1],
    chord: { freq: 82.4, type: 'square', filter: 600 },
  },
};

// E natural minor scale (Hz) — pleasant for vocals
const SCALE = [82.41, 92.50, 98.00, 110.00, 123.47, 130.81, 146.83, 164.81];

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.12;

export class KaraokeEngine {
  constructor() {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) throw new Error('Web Audio API not supported');
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;
    this.compressor = this.ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -12;
    this.compressor.knee.value = 12;
    this.compressor.ratio.value = 4;
    this.compressor.attack.value = 0.005;
    this.compressor.release.value = 0.1;
    this.master.connect(this.compressor).connect(this.ctx.destination);

    this.playing = false;
    this.preset = null;
    this.step = 0;
    this.nextNoteTime = 0;
    this._timer = null;
  }

  setVolume(v) {
    const clamped = Math.max(0, Math.min(1, v));
    this.master.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.05);
  }

  // ---------- Synthesised hits ----------
  _kick(time, preset) {
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    const startFreq = preset === 'dnb' ? 70 : 130;
    o.frequency.setValueAtTime(startFreq, time);
    o.frequency.exponentialRampToValueAtTime(40, time + 0.05);
    g.gain.setValueAtTime(0.9, time);
    g.gain.exponentialRampToValueAtTime(0.001, time + 0.45);
    o.connect(g).connect(this.master);
    o.start(time);
    o.stop(time + 0.5);
  }

  _snare(time) {
    const noise = this.ctx.createBufferSource();
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.2, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    noise.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass'; filter.frequency.value = 1500;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.55, time);
    g.gain.exponentialRampToValueAtTime(0.001, time + 0.18);
    noise.connect(filter).connect(g).connect(this.master);
    noise.start(time);
    noise.stop(time + 0.25);
  }

  _hat(time) {
    const noise = this.ctx.createBufferSource();
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.06, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    noise.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass'; filter.frequency.value = 6000;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.25, time);
    g.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
    noise.connect(filter).connect(g).connect(this.master);
    noise.start(time);
    noise.stop(time + 0.08);
  }

  _bass(time, noteIdx) {
    if (noteIdx < 0) return;
    const freq = SCALE[noteIdx] / 2; // octave down for sub
    const o = this.ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(freq, time);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(0.7, time + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, time + 0.28);
    o.connect(g).connect(this.master);
    o.start(time);
    o.stop(time + 0.3);
  }

  _pad(time, cfg) {
    // Slow chord stab every 4 steps (downbeats) for ambience.
    const o = this.ctx.createOscillator();
    o.type = cfg.type;
    o.frequency.setValueAtTime(cfg.freq, time);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cfg.filter;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(0.08, time + 0.1);
    g.gain.linearRampToValueAtTime(0.0, time + 0.6);
    o.connect(filter).connect(g).connect(this.master);
    o.start(time);
    o.stop(time + 0.65);
  }

  _scheduleStep(time) {
    const p = PRESETS[this.preset];
    if (!p) return;
    const i = this.step % 16;
    if (p.kick[i])  this._kick(time, this.preset);
    if (p.snare[i]) this._snare(time);
    if (p.hat[i])   this._hat(time);
    this._bass(time, p.bass[i]);
    if (i % 4 === 0) this._pad(time, p.chord);
    this.step++;
  }

  _tick() {
    if (!this.playing) return;
    const p = PRESETS[this.preset];
    const stepSec = 60 / p.bpm / 4;
    while (this.nextNoteTime < this.ctx.currentTime + SCHEDULE_AHEAD_S) {
      this._scheduleStep(this.nextNoteTime);
      this.nextNoteTime += stepSec;
    }
    this._timer = setTimeout(() => this._tick(), LOOKAHEAD_MS);
  }

  start(presetKey) {
    if (!PRESETS[presetKey]) return;
    this.stop();
    // Resume context (browsers require a user gesture; the caller should
    // trigger this from a click/tap event).
    if (this.ctx.state === 'suspended') this.ctx.resume();
    this.preset = presetKey;
    this.step = 0;
    this.nextNoteTime = this.ctx.currentTime + 0.05;
    this.playing = true;
    this._tick();
  }

  stop() {
    this.playing = false;
    if (this._timer) clearTimeout(this._timer);
    this._timer = null;
  }

  dispose() {
    this.stop();
    try { this.ctx.close(); } catch (err) {
      console.warn('[KaraokeEngine] dispose failed:', err?.message || err);
    }
  }
}

export const PRESET_LIST = Object.entries(PRESETS).map(([key, p]) => ({
  key, label: p.label, emoji: p.emoji, bpm: p.bpm,
}));
